import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Library, Search, Plus, Check, Eye, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Trash2, RotateCcw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminFetchChannelVideosLazyQuery,
  useAdminLibrariesQuery,
  useAdminAddToLibraryMutation,
  useAdminDeleteLibraryMutation,
  useAdminRetryFailedLibraryMutation,
  useAdminRestoreExpiredLibraryMutation,
  useAdminStatsQuery,
} from "@/generated/graphql";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime, formatDuration } from "@/lib/format";
import { CLIP_FORMATS, CLIP_FORMAT_LABEL_KEYS } from "@/lib/projects/clipFormats";

function formatViewCount(count: number | string | null | undefined): string {
  const n = Number(count);
  if (!n) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function parseISO8601DurationToSeconds(duration: string | null | undefined): number {
  if (!duration) return 0;
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 0;
  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  const seconds = parseInt(match[3] || "0", 10);
  return hours * 3600 + minutes * 60 + seconds;
}

type SortField = "name" | "curation_status" | "duration_seconds" | "language" | "createdAt";

const LIBRARY_STATUSES = ["pre-queued", "queued", "processing", "success", "failure", "expired"];

const AdminLibrary = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [channelUrl, setChannelUrl] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());

  // Settings for library creation (same as project)
  const [selectedFormat, setSelectedFormat] = useState("auto");
  const [selectedLanguage, setSelectedLanguage] = useState("pt-BR");
  const [selectedPrompt, setSelectedPrompt] = useState("clipes-virais");
  const [customPrompt, setCustomPrompt] = useState("");

  // Library list state
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const pageSize = 20;

  const [fetchChannelVideos, { data: channelData, loading: fetchingVideos }] = useAdminFetchChannelVideosLazyQuery({
    fetchPolicy: "network-only",
  });

  const { data: librariesData, loading: loadingLibraries, refetch } = useAdminLibrariesQuery({
    variables: {
      page,
      pageSize,
      search: searchQuery || undefined,
      curation_status: statusFilter || undefined,
      sortBy,
      sortOrder,
    },
    fetchPolicy: "cache-and-network",
  });

  const [addToLibrary, { loading: submitting }] = useAdminAddToLibraryMutation();
  const [deleteLibrary] = useAdminDeleteLibraryMutation();
  const [retryFailedLibrary] = useAdminRetryFailedLibraryMutation();
  const [restoreExpiredLibrary] = useAdminRestoreExpiredLibraryMutation();

  const { data: statsData } = useAdminStatsQuery();

  const chartConfig = {
    total: { label: t("admin.common.chartTotal"), color: "hsl(var(--primary))" },
    success: { label: t("admin.common.chartSuccess"), color: "hsl(142 76% 36%)" },
    failure: { label: t("admin.common.chartFailure"), color: "hsl(0 84% 60%)" },
  } satisfies ChartConfig;

  const timeSeries = statsData?.adminStats?.libraryProjects?.timeSeries || [];

  const videos = channelData?.adminFetchChannelVideos ?? [];
  const libraries = librariesData?.adminLibraries?.data || [];
  const totalPages = librariesData?.adminLibraries?.pageCount || 1;
  const total = librariesData?.adminLibraries?.total || 0;

  const promptOptions = [
    { id: "clipes-virais", label: t("admin.library.promptOptions.viralClips") },
    { id: "frases-memoraveis", label: t("admin.library.promptOptions.memorableQuotes") },
    { id: "clipes-topicos", label: t("admin.library.promptOptions.topicClips") },
    { id: "anuncios-produtos", label: t("admin.library.promptOptions.productAds") },
    { id: "depoimento", label: t("admin.library.promptOptions.testimonials") },
    { id: "outro", label: t("admin.library.promptOptions.other") },
  ];

  const getPromptText = (): string => {
    if (selectedPrompt === "outro") return customPrompt.trim();
    const option = promptOptions.find((o) => o.id === selectedPrompt);
    return option?.label || selectedPrompt;
  };

  const handleFetchVideos = async () => {
    if (!channelUrl.trim()) return;
    setIsModalOpen(true);
    setSelectedVideos(new Set());

    try {
      await fetchChannelVideos({ variables: { channelUrl: channelUrl.trim() } });
    } catch {
      toast({
        title: t("common.error"),
        description: t("admin.library.fetchError"),
        variant: "destructive",
      });
    }
  };

  const toggleVideoSelection = (videoId: string) => {
    setSelectedVideos((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedVideos.size === videos.length) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(videos.map((v) => v.id)));
    }
  };

  const handleAddSelected = async () => {
    const promptText = getPromptText();
    if (!promptText) return;

    const selectedVideoData = videos
      .filter((v) => selectedVideos.has(v.id))
      .map((v) => ({
        url: v.url,
        title: v.title,
        thumbnail: v.thumbnail || undefined,
        duration_seconds: parseISO8601DurationToSeconds(v.duration),
      }));

    if (selectedVideoData.length === 0) return;

    try {
      await addToLibrary({
        variables: {
          input: {
            videos: selectedVideoData,
            format: selectedFormat,
            language: selectedLanguage,
            prompt: promptText,
          },
        },
      });

      toast({
        title: t("admin.library.addSuccess", { count: selectedVideoData.length }),
      });

      setIsModalOpen(false);
      setChannelUrl("");
      setSelectedVideos(new Set());
      refetch();
    } catch {
      toast({
        title: t("admin.library.addError"),
        variant: "destructive",
      });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handleDeleteLibrary = async (id: string) => {
    try {
      await deleteLibrary({ variables: { id } });
      toast({ title: t("admin.library.deleteSuccess") });
      refetch();
    } catch {
      toast({ title: t("admin.library.deleteError"), variant: "destructive" });
    }
  };

  const handleRetryLibrary = async (id: string) => {
    try {
      await retryFailedLibrary({ variables: { id } });
      toast({ title: t("admin.library.retrySuccess") });
      refetch();
    } catch {
      toast({ title: t("admin.library.retryError"), variant: "destructive" });
    }
  };

  const handleRestoreLibrary = async (id: string) => {
    try {
      await restoreExpiredLibrary({ variables: { id } });
      toast({ title: t("admin.library.restoreSuccess") });
      refetch();
    } catch {
      toast({ title: t("admin.library.restoreError"), variant: "destructive" });
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "success":
        return "bg-green-500/10 text-green-500 border-green-500";
      case "processing":
      case "transcribing":
      case "segmenting":
      case "trimming":
      case "curating":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500";
      case "failure":
        return "bg-red-500/10 text-red-500 border-red-500";
      case "queued":
      case "pre-queued":
        return "bg-blue-500/10 text-blue-500 border-blue-500";
      case "expired":
        return "bg-orange-500/10 text-orange-500 border-orange-500";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500";
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Library className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t("admin.library.title")}</h1>
                <p className="text-muted-foreground">{t("admin.library.subtitle")}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">
              ADMIN
            </Badge>
          </div>

          {/* Channel Input Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("admin.library.addChannel")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t("admin.library.channelUrlPlaceholder")}
                    value={channelUrl}
                    onChange={(e) => setChannelUrl(e.target.value)}
                    className="pl-10"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleFetchVideos();
                    }}
                  />
                </div>
                <Button onClick={handleFetchVideos} disabled={!channelUrl.trim() || fetchingVideos}>
                  {fetchingVideos ? t("common.loading") : t("admin.library.fetchVideos")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Daily Chart */}
          {timeSeries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.library.dailyChart')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={timeSeries}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="total" fill="var(--color-total)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="success" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failure" fill="var(--color-failure)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          {/* Library Items Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t("admin.library.addedChannels")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t("admin.common.total")}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder={t("admin.projects.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="max-w-sm"
                />
                <Select
                  value={statusFilter || "all"}
                  onValueChange={(value) => {
                    setStatusFilter(value === "all" ? "" : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t("admin.library.status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("admin.projects.allStatuses")}</SelectItem>
                    {LIBRARY_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {loadingLibraries && !librariesData ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("name")}>
                            {t("admin.library.channelName")}<SortIcon field="name" />
                          </TableHead>
                          <TableHead>{t("admin.projects.user")}</TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("curation_status")}>
                            {t("admin.library.status")}<SortIcon field="curation_status" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("duration_seconds")}>
                            {t("admin.library.duration")}<SortIcon field="duration_seconds" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("language")}>
                            {t("admin.library.language")}<SortIcon field="language" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("createdAt")}>
                            {t("admin.library.createdAt")}<SortIcon field="createdAt" />
                          </TableHead>
                          <TableHead>{t("admin.users.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {libraries.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {item.thumbnail_url && (
                                  <img
                                    src={item.thumbnail_url}
                                    alt={item.name || ""}
                                    className="w-16 h-9 object-cover rounded"
                                  />
                                )}
                                <span className="font-medium">{item.name || "-"}</span>
                                {item.deleted && (
                                  <Badge variant="outline" className="text-red-500 border-red-500 text-xs">
                                    Deleted
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{item.created_by_user?.name || "-"}</div>
                                <div className="text-muted-foreground text-xs">
                                  {item.created_by_user?.email}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusColor(item.curation_status)}>
                                {item.curation_status || "pending"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.duration_seconds
                                ? formatDuration(item.duration_seconds)
                                : "-"}
                            </TableCell>
                            <TableCell>{item.language || "-"}</TableCell>
                            <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {item.curation_status === "failure" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRetryLibrary(item.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.retry")}
                                  </Button>
                                )}
                                {item.curation_status === "expired" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRestoreLibrary(item.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.restore")}
                                  </Button>
                                )}
                                {!item.deleted && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-500 hover:text-red-600"
                                    onClick={() => handleDeleteLibrary(item.id)}
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    {t("common.delete")}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {libraries.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                              {t("admin.library.noItems")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-sm text-muted-foreground">
                        {t("admin.common.page")} {page} {t("admin.common.of")} {totalPages}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Video Selection Modal */}
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("admin.library.modal.title")}</DialogTitle>
                <DialogDescription>{t("admin.library.modal.subtitle")}</DialogDescription>
              </DialogHeader>

              {/* Settings Section */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2">
                <div>
                  <label className="text-sm font-medium mb-1 block">{t("admin.library.format")}</label>
                  <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIP_FORMATS.map((format) => (
                        <SelectItem key={format} value={format}>{t(CLIP_FORMAT_LABEL_KEYS[format])}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{t("admin.library.language")}</label>
                  <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pt-BR">Portugues (BR)</SelectItem>
                      <SelectItem value="en-US">English (US)</SelectItem>
                      <SelectItem value="es-ES">Espanol (ES)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{t("admin.library.prompt")}</label>
                  <Select value={selectedPrompt} onValueChange={setSelectedPrompt}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {promptOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedPrompt === "outro" && (
                <Textarea
                  placeholder={t("admin.library.promptPlaceholder")}
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  className="min-h-[80px]"
                />
              )}

              {/* Select All / Deselect All */}
              {!fetchingVideos && videos.length > 0 && (
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={toggleSelectAll}>
                    {selectedVideos.size === videos.length
                      ? t("admin.library.deselectAll")
                      : t("admin.library.selectAll")}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {selectedVideos.size} / {videos.length}
                  </span>
                </div>
              )}

              {fetchingVideos ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 py-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="w-full aspect-video rounded-lg" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 py-4">
                  {videos.map((video) => {
                    const isSelected = selectedVideos.has(video.id);
                    return (
                      <div
                        key={video.id}
                        onClick={() => toggleVideoSelection(video.id)}
                        className={`cursor-pointer rounded-lg border-2 transition-all overflow-hidden ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-transparent hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="relative">
                          {video.thumbnail ? (
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              className="w-full aspect-video object-cover"
                            />
                          ) : (
                            <div className="w-full aspect-video bg-muted flex items-center justify-center">
                              <Library className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          {video.duration && (
                            <Badge className="absolute bottom-1 right-1 text-xs bg-black/70 text-white border-0">
                              {video.duration}
                            </Badge>
                          )}
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                              <Check className="w-4 h-4 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-sm font-medium line-clamp-2">{video.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {video.publishedAt && (
                              <span className="text-xs text-muted-foreground">{video.publishedAt}</span>
                            )}
                            {video.viewCount != null && Number(video.viewCount) > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                <Eye className="w-3 h-3" />
                                {formatViewCount(Number(video.viewCount))}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleAddSelected}
                  disabled={selectedVideos.size === 0 || submitting || (selectedPrompt === "outro" && !customPrompt.trim())}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {submitting
                    ? t("admin.library.submitting")
                    : t("admin.library.addSelected", { count: selectedVideos.size })}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </Layout>
  );
};

export default AdminLibrary;
